import { Property } from '../types/property.types';
import { Match } from '../types/match.types';
import { User } from '../types/user.types';

/**
 * Calculate how well a property matches a tenant pair's preferences
 * @returns Score from 0-100
 */
export const calculatePropertyMatchScore = (property: Property, match: Match): number => {
  let score = 0;

  // 1. Budget Fit (40 points max)
  if (match.budgetOverlap) {
    const budgetMid = (match.budgetOverlap.min + match.budgetOverlap.max) / 2;
    const budgetRange = match.budgetOverlap.max - match.budgetOverlap.min;

    if (property.monthlyRent >= match.budgetOverlap.min && property.monthlyRent <= match.budgetOverlap.max) {
      const priceDiff = Math.abs(property.monthlyRent - budgetMid);
      const normalizedDiff = priceDiff / (budgetRange / 2);
      score += 40 - normalizedDiff * 10;
    } else if (property.monthlyRent < match.budgetOverlap.min) {
      const underBy = (match.budgetOverlap.min - property.monthlyRent) / match.budgetOverlap.min;
      score += Math.max(0, 25 - underBy * 25);
    }
  }

  // 2. Location Match (15 points max)
  if (property.address.city.toLowerCase() === match.sharedCity.toLowerCase()) {
    score += 15;
  }

  // 3. Amenities & Lifestyle Fit (25 points max)
  const lifestyleKeywords = match.sharedLifestyle.map((l) => l.toLowerCase());
  const propertyAmenities = property.amenities.map((a) => a.toLowerCase());

  let amenityMatches = 0;
  lifestyleKeywords.forEach((keyword) => {
    if (propertyAmenities.some((amenity) => amenity.includes(keyword) || keyword.includes(amenity))) {
      amenityMatches++;
    }
  });

  if (lifestyleKeywords.length > 0) {
    score += (amenityMatches / lifestyleKeywords.length) * 25;
  }

  // 4. Occupancy Capacity (10 points max)
  if (property.maxOccupants >= 2) {
    score += 10;
  } else if (property.maxOccupants === 1) {
    score += 3;
  }

  // 5. Property Freshness (5 points max)
  const daysSinceCreated = (Date.now() - property.createdAt.toMillis()) / (1000 * 60 * 60 * 24);
  if (daysSinceCreated < 7) {
    score += 5;
  } else if (daysSinceCreated < 30) {
    score += 3;
  } else if (daysSinceCreated < 90) {
    score += 1;
  }

  // 6. Popularity Penalty (5 points max)
  if (property.views < 50) {
    score += 5;
  } else if (property.views < 100) {
    score += 3;
  } else if (property.views < 200) {
    score += 1;
  }

  return Math.round(Math.min(100, score));
};

/**
 * Calculate how well a matched pair fits a property's criteria
 * (For landlord perspective)
 */
export const calculateTenantMatchScore = (
  property: Property,
  match: Pick<Match, 'budgetOverlap' | 'compatibilityScore'>,
  user1?: User,
  user2?: User
): number => {
  let score = 0;

  // 1. Budget Qualification (40 points)
  if (match.budgetOverlap) {
    if (property.monthlyRent >= match.budgetOverlap.min && property.monthlyRent <= match.budgetOverlap.max) {
      score += 40;
    } else {
      const overBy = (property.monthlyRent - match.budgetOverlap.max) / property.monthlyRent;
      score += Math.max(0, 40 - overBy * 100);
    }
  }

  // 2. Compatibility Score (20 points)
  score += (match.compatibilityScore / 100) * 20;

  // 3. Occupancy Match (15 points)
  if (property.maxOccupants >= 2) {
    score += 15;
  }

  // 4. Lifestyle Alignment (15 points)
  if (user1 && user2 && property.amenities.length > 0) {
    const userPreferences = [...(user1.lifestyle || []), ...(user2.lifestyle || [])];

    const matches = property.amenities.filter((amenity) =>
      userPreferences.some(
        (pref) =>
          amenity.toLowerCase().includes(pref.toLowerCase()) || pref.toLowerCase().includes(amenity.toLowerCase())
      )
    );

    score += (matches.length / property.amenities.length) * 15;
  }

  // 5. Pet/Smoking Match (10 points)
  if (user1 && user2) {
    const bothWantPets = user1.petsPreference === 'yes' && user2.petsPreference === 'yes';
    const bothSmoke = user1.smokingPreference === 'yes' && user2.smokingPreference === 'yes';

    if (bothWantPets && property.petFriendly) score += 5;
    if (bothSmoke && property.smokingAllowed) score += 5;
    if (!bothWantPets && !bothSmoke) score += 5;
  }

  return Math.round(Math.min(100, score));
};

/**
 * Filter properties that meet minimum criteria for a match
 */
export const filterEligibleProperties = (properties: Property[], match: Match): Property[] => {
  return properties.filter((property) => {
    if (property.status !== 'available') return false;

    if (property.address.city.toLowerCase() !== match.sharedCity.toLowerCase()) {
      return false;
    }

    if (match.budgetOverlap) {
      if (
        property.monthlyRent < match.budgetOverlap.min * 0.8 ||
        property.monthlyRent > match.budgetOverlap.max * 1.1
      ) {
        return false;
      }
    }

    if (property.maxOccupants < 1) return false;

    return true;
  });
};

/**
 * Sort properties by match score
 */
export const sortPropertiesByMatch = (properties: Property[], match: Match): Property[] => {
  return [...properties].sort((a, b) => {
    const scoreA = calculatePropertyMatchScore(a, match);
    const scoreB = calculatePropertyMatchScore(b, match);
    return scoreB - scoreA;
  });
};

/**
 * Get property recommendation summary for display
 */
export const getPropertySummary = (property: Property, match: Match) => {
  const score = calculatePropertyMatchScore(property, match);

  let recommendation = 'Good match';
  if (score >= 80) recommendation = 'Excellent match!';
  else if (score >= 60) recommendation = 'Great match';
  else if (score >= 40) recommendation = 'Good match';
  else if (score >= 20) recommendation = 'Possible match';
  else recommendation = 'Consider carefully';

  const reasons: string[] = [];

  if (match.budgetOverlap) {
    const inBudget =
      property.monthlyRent >= match.budgetOverlap.min && property.monthlyRent <= match.budgetOverlap.max;
    if (inBudget) {
      reasons.push('Within your budget');
    } else if (property.monthlyRent < match.budgetOverlap.min) {
      reasons.push('Below budget (good deal!)');
    }
  }

  const lifestyleMatches = match.sharedLifestyle.filter((pref) =>
    property.amenities.some(
      (amenity) =>
        amenity.toLowerCase().includes(pref.toLowerCase()) || pref.toLowerCase().includes(amenity.toLowerCase())
    )
  );

  if (lifestyleMatches.length > 0) {
    reasons.push(`Matches ${lifestyleMatches.length} of your preferences`);
  }

  if (property.maxOccupants >= 2) {
    reasons.push('Perfect for roommates');
  }

  const daysSinceCreated = (Date.now() - property.createdAt.toMillis()) / (1000 * 60 * 60 * 24);
  if (daysSinceCreated < 7) {
    reasons.push('New listing');
  }

  return {
    score,
    recommendation,
    reasons,
  };
};
